import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import LightGestion from 'components/headers/lightGestion';
import { Modal, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';

const load = (k, def) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : def; } catch { return def; } };

const toAbsImageUrl = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  if (s.startsWith('data:') || s.startsWith('blob:') || /^https?:\/\//i.test(s)) return s;
  const BASE = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:8000';
  if (s.startsWith('/storage/')) return `${BASE}${s}`;
  if (s.startsWith('storage/')) return `${BASE}/${s}`;
  return `${BASE}/${s.replace(/^\//, '')}`;
};

const Facture = () => {
  const { t } = useTranslation();
  const [showType, setShowType] = useState(true);
  const [type, setType] = useState('produit'); 
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState({ id: null, name: '', address: '', phone: '', ice: '' });
  const [rows, setRows] = useState([{ designation: '', tva: 0, pu: 0, qty: 1 }]);
  const [refNo, setRefNo] = useState(() => `FAC-${new Date().getFullYear()}-${String(Math.floor(Math.random()*1000)).padStart(4,'0')}`);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0,10));
  const [previewOpen, setPreviewOpen] = useState(false);
  const invoiceRef = useRef(null);
  const [company, setCompany] = useState(load('company.info', { name: '', phone: '', email: '', ice: '', website: '', address: '', logo: '', rc: '', if: '', tp: '' }));

  const currency = useCallback((v) => `${Number(v || 0).toFixed(2)} ${t('stock.currency', 'MAD')}`,[t]);

  const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/clientgestions', {
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      });
      const raw = Array.isArray(res.data) ? res.data : (res.data.data || res.data.clients || []);
      setClients(raw.map(r => ({
        id: r.id || r.client_id,
        name: String(r.name || r.Nom || ''),
        address: r.Address || '',
        phone: r.Telephone || '',
        ice: r.ICE || ''
      })));
    } catch (e) {}
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

const getSociete = async () => {
  const token = localStorage.getItem('authToken');
  const res = await axios.get(
    'https://plateformewmg.cloud/api/Gestion/societes',
    {
      headers: { Authorization: token ? `Bearer ${token}` : '' }
    }
  );
  return res.data?.data?.[0] || null;
};
useEffect(() => {
  getSociete().then((raw) => {
    if (!raw) return;

    setCompany({
      name: raw.Nom || '',
      phone: raw.Telephone || '',
      address: raw.Address || '',
      website: raw.SiteWeb || '',
      email: raw.Email || '',
      rc: raw.RC || '',
      ice: raw.ICE || '',
      if: raw.IF || '',
      tp: raw.TP || '',
      logo: raw.logo_url || ''
    });
  });
}, []);

  const addRow = () => setRows(r => [...r, { designation: '', tva: 0, pu: 0, qty: 1 }]);
  const delRow = (idx) => setRows(r => r.filter((_,i) => i !== idx));
  const setRow = (idx, patch) => setRows(r => r.map((row,i) => i===idx ? { ...row, ...patch } : row));

  const totals = useMemo(() => {
    const ht = rows.reduce((s, r) => s + Number(r.pu||0)*Number(r.qty||0), 0);
    const tva = rows.reduce((s, r) => s + (Number(r.pu||0)*Number(r.qty||0)) * (Number(r.tva||0)/100), 0);
    return { ht, tva, ttc: ht + tva };
  }, [rows]);

  const handleDownload = async () => {
  const node = invoiceRef.current;
  if (!node) return;
  
  // 1. Drop scale slightly to 2 (still crisp for print, but 2x fewer pixels than 3)
  const canvas = await html2canvas(node, { 
    scale: 2, 
    useCORS: true,
    logging: false 
  });
  
  // 2. Change 'image/png' to 'image/jpeg' and set compression quality to 0.75
  const imgData = canvas.toDataURL('image/jpeg', 0.75); 
  
  const pdf = new jsPDF('p', 'mm', 'a4');
  
  // 3. Match the format here ('JPEG')
  pdf.addImage(imgData, 'JPEG', 0, 0, 210, (canvas.height * 210) / canvas.width);
  pdf.save(`${refNo}.pdf`);
};
  return (
    <AnimationRevealPage>
      <LightGestion />
      <div className="container mt-4" style={{ maxWidth: 1000 }}>
        {/* Formulaire simplifié pour l'UI */}
        <div className="card p-4 shadow-sm mb-5">
           <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="fw-bold">{t('invoice.title', 'Nouvelle Facture')}</h2>
              <Button variant="outline-primary" onClick={() => setShowType(true)}>{t('invoice.changeType', 'Changer le type')}</Button>
           </div>
           <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small fw-bold">RÉFÉRENCE</label>
                <input className="form-control" value={refNo} onChange={e => setRefNo(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">DATE</label>
                <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">CLIENT</label>
                <Form.Control list="list-c" value={client.name} onChange={e => {
                  const found = clients.find(c => c.name === e.target.value);
                  setClient(found || { ...client, name: e.target.value });
                }} />
                <datalist id="list-c">{clients.map((c, i) => <option key={i} value={c.name} />)}</datalist>
              </div>
           </div>

           <table className="table mt-4">
              <thead>
                <tr className="table-light">
                  <th>Désignation</th>
                  <th>TVA %</th>
                  <th>PU HT</th>
                  <th>Qté</th>
                  <th className="text-end">Total HT</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td><input className="form-control" value={r.designation} onChange={e => setRow(i, { designation: e.target.value })} /></td>
                    <td style={{ width: 100 }}><input type="number" className="form-control" value={r.tva} onChange={e => setRow(i, { tva: e.target.value })} /></td>
                    <td style={{ width: 120 }}><input type="number" className="form-control" value={r.pu} onChange={e => setRow(i, { pu: e.target.value })} /></td>
                    <td style={{ width: 100 }}><input type="number" className="form-control" value={r.qty} onChange={e => setRow(i, { qty: e.target.value })} /></td>
                    <td className="text-end fw-bold">{currency(r.pu * r.qty)}</td>
                    <td><Button variant="link" className="text-danger" onClick={() => delRow(i)}>✕</Button></td>
                  </tr>
                ))}
              </tbody>
           </table>
           <Button variant="link" onClick={addRow}>+ Ajouter une ligne</Button>

           <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={() => setPreviewOpen(true)}>Aperçu</Button>
              <Button variant="primary" onClick={handleDownload}>Télécharger PDF</Button>
           </div>
        </div>

        {/* --- SECTION DESIGN PDF (A4) --- */}
        <div 
          ref={invoiceRef}
          style={{
            width: '210mm', minHeight: '297mm', background: '#fff', padding: '20mm',
            display: 'flex', flexDirection: 'column', fontFamily: 'sans-serif', color: '#333'
          }}
        >
          {/* Header */}
          <div className="d-flex justify-content-between align-items-start mb-5">
            <div>
              {company.logo ? <img src={company.logo} alt="logo" crossOrigin="anonymous" style={{ height: 60 }} /> : <h2 className="fw-bold text-primary">WMG</h2>}
            </div>
            <div className="text-end">
              <h4 className="fw-bold mb-0 text-primary" style={{ fontSize: '22px' }}>{t('invoice.pdf.invoice','Facture')}</h4>
              <div className="small text-muted">Réf.: {refNo}</div>
              <div className="small text-muted">Date : {new Date(date).toLocaleDateString()}</div>
            </div>
          </div>

          {/* Adresses */}
          <div className="row mb-5">
            <div className="col-6">
              <div style={{ borderLeft: '3px solid #2563eb', paddingLeft: '15px' }}>
                <div className="small text-muted fw-bold mb-1">ÉMETTEUR :</div>
                <div className="fw-bold fs-5">{company.name}</div>
                <div className="small">{company.email}</div>
                <div className="small">{company.phone}</div>
                <div className="small">{company.website}</div>
              </div>
            </div>
            <div className="col-6 text-end">
              <div style={{ borderRight: '3px solid #ddd', paddingRight: '15px' }}>
                <div className="small text-muted fw-bold mb-1">ADRESSÉ À :</div>
                <div className="fw-bold fs-5">{client.name}</div>
                <div className="small">{client.address}</div>
                {client.ice && <div className="small">ICE: {client.ice}</div>}
              </div>
            </div>
          </div>

          {/* Table */}
          <div style={{ flex: 1 }}>
            <table className="table" style={{ fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #eee' }}>
                  <th className="py-3 px-2">Désignation</th>
                  <th className="text-center py-3">TVA</th>
                  <th className="text-center py-3">PU HT</th>
                  <th className="text-center py-3">Qté</th>
                  <th className="text-end py-3 px-2">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td className="py-3 px-2">{r.designation}</td>
                    <td className="text-center py-3">{r.tva}%</td>
                    <td className="text-center py-3">{Number(r.pu).toFixed(2)}</td>
                    <td className="text-center py-3">{r.qty}</td>
                    <td className="text-end py-3 px-2 fw-bold">{Number(r.pu * r.qty).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totaux */}
            <div className="d-flex justify-content-end mt-4">
              <div style={{ width: '250px' }}>
                <div className="d-flex justify-content-between py-1 border-bottom small">
                  <span>Total HT</span><span>{Number(totals.ht).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between py-1 border-bottom small">
                  <span>TVA</span><span>{Number(totals.tva).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between py-3 fw-bold" style={{ fontSize: '18px' }}>
                  <span>TOTAL TTC</span><span>{Number(totals.ttc).toFixed(2)} MAD</span>
                </div>
              </div>
            </div>

            {/* Signature Box */}
            <div className="d-flex justify-content-end mt-5">
              <div style={{ 
                border: '1px solid #e2e8f0', borderRadius: '12px', width: '300px', 
                height: '120px', padding: '15px', background: '#fcfcfc'
              }}>
                <span className="small text-muted" style={{ fontSize: '10px' }}>
                  Cachet et/ou signature du client pour accord
                </span>
              </div>
            </div>
          </div>

          {/* Footer Centré */}
          <div className="text-center mt-auto pt-4" style={{ borderTop: '1px solid #000000' }}>
            <div className="text-muted" style={{ fontSize: '10px' }}>
              {company.address} | Tél: {company.phone} | {company.email}
            </div>
            <div className="text-muted" style={{ fontSize: '10px' }}>
              RC: {company.rc} | IF: {company.if} | ICE: {company.ice}| Patente: {company.tp}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Choix Type */}
      <Modal show={showType} onHide={() => setShowType(false)} centered>
        <Modal.Header closeButton><Modal.Title>Type de Facture</Modal.Title></Modal.Header>
        <Modal.Body className="d-grid gap-2 p-4">
          <Button size="lg" onClick={() => { setType('produit'); setShowType(false); }}>📦 Produits</Button>
          <Button size="lg" variant="outline-primary" onClick={() => { setType('service'); setShowType(false); }}>🛠️ Services</Button>
        </Modal.Body>
      </Modal>

      {/* Modal Aperçu */}
      <Modal show={previewOpen} onHide={() => setPreviewOpen(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Aperçu</Modal.Title></Modal.Header>
        <Modal.Body className="bg-dark p-0 overflow-auto" style={{ maxHeight: '80vh' }}>
          <div style={{ transform: 'scale(0.6)', transformOrigin: 'top center', margin: '0 auto' }}>
            {/* Le contenu est rendu ici via la ref invoiceRef */}
          </div>
        </Modal.Body>
      </Modal>
    </AnimationRevealPage>
  );
};

export default Facture;